import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiMapPin, FiDollarSign, FiCalendar, FiHeart, FiUserPlus, FiFilter, FiSearch } = FiIcons;

const CRM = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const clients = [
    {
      id: 1,
      name: 'Sara K.',
      location: 'Laval',
      ltv: '$280.00',
      lastBooking: 'June 29',
      sentiment: 'Positive',
      referralSource: 'Google Search',
      preferredService: 'Cleaning',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face'
    },
    {
      id: 2,
      name: 'Mike Chen',
      location: 'Montreal',
      ltv: '$450.00',
      lastBooking: 'July 15',
      sentiment: 'Positive',
      referralSource: 'Referral',
      preferredService: 'Gardening',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face'
    },
    {
      id: 3,
      name: 'Lisa R.',
      location: 'Laval',
      ltv: '$320.00',
      lastBooking: 'July 8',
      sentiment: 'Neutral',
      referralSource: 'WhatsApp',
      preferredService: 'Pet Sitting',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face'
    },
    {
      id: 4,
      name: 'David W.',
      location: 'Montreal',
      ltv: '$180.00',
      lastBooking: 'June 22',
      sentiment: 'Positive',
      referralSource: 'Flyer',
      preferredService: 'Tutoring',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face'
    }
  ];

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'Positive': return 'bg-green-100 text-green-800';
      case 'Neutral': return 'bg-yellow-100 text-yellow-800';
      case 'Negative': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'all') return matchesSearch;
    if (filter === 'new') return matchesSearch && new Date(client.lastBooking) > new Date('2024-07-01');
    if (filter === 'high-ltv') return matchesSearch && parseFloat(client.ltv.replace('$', '')) > 300;
    if (filter === 'positive') return matchesSearch && client.sentiment === 'Positive';
    
    return matchesSearch;
  });

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <motion.div 
        className="flex items-center justify-between"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h1 className="text-3xl font-bold text-slate-800">CRM</h1>
          <p className="text-slate-600 mt-1">Your client relationships in one place</p>
        </div>
        
        <motion.button
          className="flex items-center gap-2 bg-trust-500 text-white px-6 py-3 rounded-lg hover:bg-trust-600 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <SafeIcon icon={FiUserPlus} />
          Add Client
        </motion.button>
      </motion.div>

      {/* Search and Filters */}
      <motion.div 
        className="flex flex-col sm:flex-row gap-4 bg-white rounded-xl p-4 border border-slate-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="flex-1 relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trust-500"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiFilter} className="text-slate-500" />
          <div className="flex gap-2">
            {[
              { key: 'all', label: 'All' },
              { key: 'new', label: 'New Clients' },
              { key: 'high-ltv', label: 'High LTV' },
              { key: 'positive', label: 'Positive Feedback' }
            ].map((filterType) => (
              <button
                key={filterType.key}
                onClick={() => setFilter(filterType.key)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                  filter === filterType.key
                    ? 'bg-trust-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {filterType.label}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Client Table */}
      <motion.div 
        className="bg-white rounded-xl border border-slate-200 overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 font-semibold text-slate-700">Client</th>
                <th className="text-left p-4 font-semibold text-slate-700">Location</th>
                <th className="text-left p-4 font-semibold text-slate-700">LTV</th>
                <th className="text-left p-4 font-semibold text-slate-700">Last Booking</th>
                <th className="text-left p-4 font-semibold text-slate-700">Sentiment</th>
                <th className="text-left p-4 font-semibold text-slate-700">Referral Source</th>
                <th className="text-left p-4 font-semibold text-slate-700">Preferred Service</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client, index) => (
                <motion.tr
                  key={client.id}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{ backgroundColor: '#f8fafc' }}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img 
                        src={client.avatar} 
                        alt={client.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-medium text-slate-800">{client.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiMapPin} className="text-slate-400" />
                      <span className="text-slate-600">{client.location}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiDollarSign} className="text-green-500" />
                      <span className="font-medium text-slate-800">{client.ltv}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiCalendar} className="text-slate-400" />
                      <span className="text-slate-600">{client.lastBooking}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getSentimentColor(client.sentiment)}`}>
                      <SafeIcon icon={FiHeart} className="text-xs" />
                      {client.sentiment}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-slate-600">{client.referralSource}</span>
                  </td>
                  <td className="p-4">
                    <span className="bg-trust-100 text-trust-800 px-2 py-1 rounded-full text-xs font-medium">
                      {client.preferredService}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {filteredClients.length === 0 && (
        <motion.div 
          className="text-center py-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <SafeIcon icon={FiUser} className="text-6xl text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-600 mb-2">No clients found</h3>
          <p className="text-slate-500">Try adjusting your search or filters.</p>
        </motion.div>
      )}
    </div>
  );
};

export default CRM;